import { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import InteractionHints from './InteractionHints';
import { applyFabricTexture, updateTextureZoom, remapExtrudeUVs } from './fabricTexture';
import FabricLoadingOverlay from './FabricLoadingOverlay';

const NORM = 125;
const DEFAULT_CAM = { x: -1.2, y: 0.45, z: 0.6 };

function createWaterfallProfile(w, t) {
  const halfW = w / 2;
  const rFront = Math.min(2 / NORM, t / 2 * 0.95, halfW * 0.4);
  const rBack = Math.min(1 / NORM, t / 2 * 0.95, halfW * 0.4);

  const s = new THREE.Shape();
  s.moveTo(halfW - rBack, 0);
  s.lineTo(-halfW + rFront, 0);
  s.absarc(-halfW + rFront, rFront, rFront, -Math.PI / 2, Math.PI, true);
  s.lineTo(-halfW, t - rFront);
  s.absarc(-halfW + rFront, t - rFront, rFront, Math.PI, Math.PI / 2, true);
  s.lineTo(halfW - rBack, t);
  s.absarc(halfW - rBack, t - rBack, rBack, Math.PI / 2, 0, true);
  s.lineTo(halfW, rBack);
  s.absarc(halfW - rBack, rBack, rBack, 0, -Math.PI / 2, true);
  return s;
}

function buildGeo(l, w, t) {
  const profile = createWaterfallProfile(w, t);
  const bev = Math.min(l * 0.012, 0.003);
  const geo = new THREE.ExtrudeGeometry(profile, {
    depth: l, bevelEnabled: true, bevelThickness: bev,
    bevelSize: bev, bevelSegments: 3, curveSegments: 32
  });
  // Flat orientation first
  geo.rotateY(-Math.PI / 2);
  geo.translate(l / 2, 0, 0);
  // Stand upright: swap Y and Z so zip (was +Z) goes to bottom (Y=0), waterfall (was -Z) goes to top
  geo.rotateX(Math.PI / 2);
  geo.translate(0, w / 2, 0);
  remapExtrudeUVs(geo);
  geo.computeVertexNormals();
  return geo;
}

const WaterfallBackCushion3DPreview = ({
  length = 100, width = 50, thickness = 8, rawThickness,
  fabricColor, fabricImageUrl, fabricZoom = 3,
  showZip = true, zipPlacement = 'length',
  rawLength = 0, rawWidth = 0, unit = 'cm',
  activeField = null,
  pipingOption = 'none', pipingColor, pipingImageUrl
}) => {
  const mountRef = useRef(null);
  const [fabricLoading, setFabricLoading] = useState(false);
  const [pipingLoading, setPipingLoading] = useState(false);
  const rendererRef = useRef(null);
  const cushionRef = useRef(null);
  const zipRef = useRef(null);
  const dimLabelsRef = useRef({ width: null, height: null, thickness: null });
  const currentDimsRef = useRef(null);
  const targetDimsRef = useRef(null);
  const containerSizeRef = useRef({ w: 0, h: 0 });
  const initializedRef = useRef(false);
  const animFrameRef = useRef(null);
  const resetCameraRef = useRef(true);
  const showZipRef = useRef(showZip);
  const activeFieldRef = useRef(activeField);
  const zipPlacementRef = useRef(zipPlacement);
  const pipingLeftRef = useRef(null);
  const pipingRightRef = useRef(null);

  useEffect(() => { showZipRef.current = showZip; }, [showZip]);
  useEffect(() => { activeFieldRef.current = activeField; }, [activeField]);
  useEffect(() => { zipPlacementRef.current = zipPlacement; }, [zipPlacement]);

  useEffect(() => {
    if (!mountRef.current || initializedRef.current) return;
    initializedRef.current = true;
    const container = mountRef.current;
    const cw = container.clientWidth, ch = container.clientHeight;
    containerSizeRef.current = { w: cw, h: ch };

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf5f0eb);

    const camera = new THREE.PerspectiveCamera(35, cw / ch, 0.01, 100);
    camera.position.set(DEFAULT_CAM.x, DEFAULT_CAM.y, DEFAULT_CAM.z);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setSize(cw, ch);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enablePan = true;
    controls.screenSpacePanning = true;
    controls.panSpeed = 1.2;
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.minDistance = 0.15;
    controls.maxDistance = 4;
    controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN };
    controls.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.PAN, RIGHT: THREE.MOUSE.PAN };
    controls.target.set(0, 0, 0);
    controls.addEventListener('start', () => { resetCameraRef.current = false; });
    renderer.domElement.__orbitControls = controls;
    renderer.domElement.__renderFrame = () => renderer.render(scene, camera);

    scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    const dir1 = new THREE.DirectionalLight(0xffffff, 1.2);
    dir1.position.set(3, 5, 3);
    dir1.castShadow = true;
    dir1.shadow.mapSize.set(1024, 1024);
    scene.add(dir1);
    scene.add(new THREE.DirectionalLight(0xffffff, 0.4).translateX(-2).translateY(3).translateZ(-1));
    scene.add(new THREE.PointLight(0xffffff, 0.3).translateY(2));

    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(5, 5),
      new THREE.ShadowMaterial({ opacity: 0.15 })
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -0.05;
    floor.receiveShadow = true;
    scene.add(floor);

    const l0 = length / NORM, w0 = width / NORM, t0 = thickness / NORM;
    const mat = new THREE.MeshStandardMaterial({ color: 0xc8b89a, roughness: 0.85, metalness: 0.02 });
    const mesh = new THREE.Mesh(buildGeo(l0, w0, t0), mat);
    mesh.castShadow = true;
    scene.add(mesh);
    cushionRef.current = mesh;

    currentDimsRef.current = { l: l0, w: w0, t: t0 };
    targetDimsRef.current = { l: l0, w: w0, t: t0 };

    // Zip - larger for visibility on bottom face
    const trackMat = new THREE.MeshBasicMaterial({ color: 0xf5f5f5 });
    const zipTrack = new THREE.Mesh(new THREE.BoxGeometry(1, 0.008, 0.012), trackMat);
    scene.add(zipTrack);
    const zipTeeth = Array.from({ length: 48 }, () => {
      const tooth = new THREE.Mesh(new THREE.BoxGeometry(0.012, 0.008, 0.008), trackMat);
      scene.add(tooth);
      return tooth;
    });
    const zipPull = new THREE.Mesh(new THREE.BoxGeometry(0.035, 0.007, 0.008), new THREE.MeshBasicMaterial({ color: 0xf5f5f5 }));
    scene.add(zipPull);
    zipRef.current = { track: zipTrack, teeth: zipTeeth, pull: zipPull };

    // Side piping meshes (left + right faces)
    const pipingMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.6, metalness: 0.05 });
    const pipingLeft = new THREE.Mesh(new THREE.BufferGeometry(), pipingMat);
    const pipingRight = new THREE.Mesh(new THREE.BufferGeometry(), pipingMat.clone());
    pipingLeft.visible = false;
    pipingRight.visible = false;
    scene.add(pipingLeft);
    scene.add(pipingRight);
    pipingLeftRef.current = pipingLeft;
    pipingRightRef.current = pipingRight;

    const targetCamPos = new THREE.Vector3();

    const animate = () => {
      animFrameRef.current = requestAnimationFrame(animate);
      // Thumbnail mode: hide dimension indicators and zip
      if (typeof window !== 'undefined' && window.__THUMBNAIL_MODE__) {
        const __zr = zipRef.current;
        if (__zr) {
          if (__zr.track) __zr.track.visible = false;
          if (__zr.teeth) __zr.teeth.forEach(t => t.visible = false);
          if (__zr.pull) __zr.pull.visible = false;
        }
      }
      controls.update();

      const cd = currentDimsRef.current;
      const td = targetDimsRef.current;
      if (!cd || !td) { renderer.render(scene, camera); return; }

      let needsRebuild = false;
      const next = {};
      for (const key of ['l', 'w', 't']) {
        next[key] = THREE.MathUtils.lerp(cd[key], td[key], 0.1);
        if (Math.abs(next[key] - td[key]) > 0.0001) needsRebuild = true;
      }

      if (needsRebuild || true) {
        mesh.geometry.dispose();
        mesh.geometry = buildGeo(next.l, next.w, next.t);
        currentDimsRef.current = next;
      }

      // Track piping side positions with lerped dims
      if (pipingLeftRef.current && pipingLeftRef.current.visible) {
        const bev = Math.min(next.l * 0.012, 0.003);
        const pX = next.l / 2 + bev * Math.SQRT1_2;
        pipingLeftRef.current.position.x = -pX;
        pipingRightRef.current.position.x = pX;
      }

      // Floor
      floor.position.y = -0.02;

      // Upright geometry: X=[-l/2, l/2], Y=[0, w], Z=[0, t]
      const halfL = next.l / 2;
      const halfT = next.t / 2;

      // Zip - on the bottom edge (Y≈0)
      const zr = zipRef.current;
      const vis = showZipRef.current;
      const zp = zipPlacementRef.current;

      if (zp === 'length') {
        // Bottom edge: runs along X at center of thickness, protruding downward
        const zipLen = next.l * 0.98;
        zr.track.visible = vis;
        zr.track.scale.x = zipLen;
        zr.track.position.set(0, -0.004, halfT);
        zr.track.rotation.set(0, 0, 0);
        zr.pull.visible = vis;
        zr.pull.position.set(halfL * 0.96, -0.012, halfT);
        zr.pull.rotation.set(0, 0, 0);
        for (let i = 0; i < zr.teeth.length; i++) {
          zr.teeth[i].visible = vis;
          const xp = -zipLen / 2 + zipLen * ((i + 0.5) / zr.teeth.length);
          const yOff = (i % 2 === 0 ? 1 : -1) * 0.010;
          zr.teeth[i].position.set(xp, -0.004 + yOff, halfT);
        }
      } else {
        // Side edge: runs along Y (height) on front face
        const zipLen = next.w * 0.98;
        zr.track.visible = vis;
        zr.track.scale.x = zipLen;
        zr.track.position.set(halfL + 0.001, next.w / 2, next.t + 0.001);
        zr.track.rotation.set(0, 0, Math.PI / 2);
        zr.pull.visible = vis;
        zr.pull.position.set(halfL + 0.008, next.w * 0.96, next.t + 0.001);
        zr.pull.rotation.set(0, 0, Math.PI / 2);
        for (let i = 0; i < zr.teeth.length; i++) {
          zr.teeth[i].visible = vis;
          const yp = zipLen * 0.01 + zipLen * ((i + 0.5) / zr.teeth.length);
          const xOff = (i % 2 === 0 ? 1 : -1) * 0.010;
          zr.teeth[i].position.set(halfL + 0.001 + xOff, yp, next.t + 0.001);
        }
      }

      // Labels
      const { w: cw2, h: ch2 } = containerSizeRef.current;
      const af = activeFieldRef.current;
      const activeBg = 'rgba(255,215,0,0.92)';
      const defaultBg = 'rgba(255,255,255,0.85)';
      const activeText = '#1a1a1a';
      const defaultText = '#374151';

      const project = (pos) => {
        const v = pos.clone().project(camera);
        return { x: (v.x * 0.5 + 0.5) * cw2, y: (-v.y * 0.5 + 0.5) * ch2 };
      };
      const setLabel = (el, isActive, worldPos) => {
        if (!el) return;
        const p = project(worldPos);
        el.style.left = `${p.x}px`;
        el.style.top = `${p.y}px`;
        el.style.opacity = '1';
        el.style.backgroundColor = isActive ? activeBg : defaultBg;
        el.style.color = isActive ? activeText : defaultText;
      };

      // Width label (left-right, X) — at bottom-front
      setLabel(dimLabelsRef.current.width, af === 'length',
        new THREE.Vector3(0, -0.02, next.t + 0.03));
      // Height label (up-down, Y) — on the left side
      setLabel(dimLabelsRef.current.height, af === 'width',
        new THREE.Vector3(-halfL - 0.03, next.w / 2, halfT));
      // Thickness label — on the right side at mid-height
      setLabel(dimLabelsRef.current.thickness, af === 't',
        new THREE.Vector3(halfL + 0.03, next.w * 0.15, halfT));

      // Camera
      if (resetCameraRef.current) {
        const maxDim = Math.max(next.l, next.w);
        const isMobile = cw2 < 500;
        if (isMobile) {
          const distMult = 1.6;
          const dist = maxDim * distMult + 0.35;
          const camScale = 1.4;
          targetCamPos.set(
            DEFAULT_CAM.x / camScale * dist,
            DEFAULT_CAM.y / camScale * dist,
            DEFAULT_CAM.z / camScale * dist
          );
          camera.position.lerp(targetCamPos, 0.15);
          controls.target.set(0.03, next.w / 4, halfT);
        } else {
          // Desktop: exact user-tuned pose.
          targetCamPos.set(-1.1566, 0.5539, 0.64);
          camera.position.lerp(targetCamPos, 0.15);
          controls.target.set(-0.0037, 0.1161, -0.0704);
        }
        if (camera.position.distanceTo(targetCamPos) < 0.02) { resetCameraRef.current = false; controls.saveState(); }
      }

      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      const nw = container.clientWidth, nh = container.clientHeight;
      containerSizeRef.current = { w: nw, h: nh };
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
      renderer.setSize(nw, nh);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener('resize', onResize);
      controls.dispose();
      renderer.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
      initializedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!targetDimsRef.current) return;
    targetDimsRef.current = { l: length / NORM, w: width / NORM, t: thickness / NORM };
    resetCameraRef.current = true;
  }, [length, width, thickness]);

  useEffect(() => {
    if (cushionRef.current) {
      const needsLoad = fabricImageUrl && (fabricImageUrl.includes('warwick.com') || fabricImageUrl.startsWith('/api/fabric-images'));
      if (needsLoad) setFabricLoading(true);
      applyFabricTexture(cushionRef.current, fabricImageUrl, fabricColor, fabricZoom, () => setFabricLoading(false));
    }
  }, [fabricColor, fabricImageUrl]);

  useEffect(() => {
    if (cushionRef.current) updateTextureZoom(cushionRef.current, fabricZoom);
  }, [fabricZoom]);

  // Update side piping rings (trace waterfall cross-section on left/right faces, standing upright)
  useEffect(() => {
    const pl = pipingLeftRef.current;
    const pr = pipingRightRef.current;
    if (!pl || !pr) return;

    const showPiping = pipingOption !== 'none';
    pl.visible = showPiping;
    pr.visible = showPiping;
    if (!showPiping) { setPipingLoading(false); return; }

    const pColor = pipingOption === 'same' ? (fabricColor || '#c8b89a') : (pipingColor || '#f5f0eb');
    const pImageUrl = pipingOption === 'same' ? fabricImageUrl : pipingImageUrl;
    const needsLoad = pImageUrl && (pImageUrl.includes('warwick.com') || pImageUrl.startsWith('/api/fabric-images'));
    if (needsLoad) setPipingLoading(true);
    let __remaining = 2;
    const __done = () => { __remaining -= 1; if (__remaining <= 0) setPipingLoading(false); };
    applyFabricTexture(pl, pImageUrl, pColor, fabricZoom, needsLoad ? __done : null);
    applyFabricTexture(pr, pImageUrl, pColor, fabricZoom, needsLoad ? __done : null);

    const l = length / NORM, w = width / NORM, t = thickness / NORM;
    const bev = Math.min(l * 0.012, 0.003);
    const tubeRadius = 0.003;

    // Get waterfall profile outline: profile X=[-halfW, halfW], Y=[0, t]
    // Upright mapping: profile (px, py) → world (0, px + w/2, py)
    const profile = createWaterfallProfile(w, t);
    const pts2D = profile.getPoints(64);
    if (pts2D.length > 1 && pts2D[0].distanceTo(pts2D[pts2D.length - 1]) < 0.0001) pts2D.pop();

    const centerY = w / 2;
    const centerZ = t / 2;
    const pts3D = pts2D.map(p => new THREE.Vector3(0, p.x + w / 2, p.y));

    // Expand outward in YZ plane
    const expansion = bev * Math.SQRT1_2;
    const n = pts3D.length;
    const expanded = pts3D.map((pt, i) => {
      const prev = pts3D[(i - 1 + n) % n];
      const next2 = pts3D[(i + 1) % n];
      const dy = next2.y - prev.y;
      const dz = next2.z - prev.z;
      const len = Math.sqrt(dy * dy + dz * dz) || 1;
      const ny = dz / len;
      const nz = -dy / len;
      const dot = ny * (pt.y - centerY) + nz * (pt.z - centerZ);
      const sign = dot >= 0 ? 1 : -1;
      return new THREE.Vector3(0, pt.y + sign * ny * expansion, pt.z + sign * nz * expansion);
    });

    class LinearClosedCurve extends THREE.Curve {
      constructor(pts) { super(); this.pts = pts; }
      getPoint(t2) {
        const n2 = this.pts.length;
        const f = t2 * n2;
        const i = Math.floor(f) % n2;
        const a = f - Math.floor(f);
        return new THREE.Vector3().lerpVectors(this.pts[i], this.pts[(i + 1) % n2], a);
      }
    }

    const curve = new LinearClosedCurve(expanded);
    const tubeGeo = new THREE.TubeGeometry(curve, 128, tubeRadius, 8, true);

    pl.geometry.dispose();
    pr.geometry.dispose();
    pl.geometry = tubeGeo;
    pr.geometry = tubeGeo.clone();

    const pX = l / 2 + expansion;
    pl.position.x = -pX;
    pr.position.x = pX;
  }, [length, width, thickness, fabricColor, pipingOption, pipingColor, pipingImageUrl, fabricImageUrl, fabricZoom]);

  const handleReset = () => { resetCameraRef.current = true; };

  return (
    <div className="w-full h-[280px] sm:h-[480px] rounded-xl overflow-hidden relative" style={{ background: 'linear-gradient(135deg, #f5f0eb 0%, #e8e0d8 100%)' }} data-testid="waterfall-back-3d-preview">
      {fabricLoading && <FabricLoadingOverlay />}
      {!fabricLoading && pipingLoading && <FabricLoadingOverlay label="Loading piping..." />}
      <div ref={mountRef} style={{ width: '100%', height: '100%' }} />
      <div ref={el => dimLabelsRef.current.width = el} className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0 }} data-testid="dim-label-width">
        {rawLength > 0 ? `${unit === 'mm' ? rawLength * 10 : rawLength} ${unit}` : `W ${unit}`}
      </div>
      <div ref={el => dimLabelsRef.current.height = el} className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0 }} data-testid="dim-label-height">
        {rawWidth > 0 ? `${unit === 'mm' ? rawWidth * 10 : rawWidth} ${unit}` : `H ${unit}`}
      </div>
      <div ref={el => dimLabelsRef.current.thickness = el} className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0 }} data-testid="dim-label-thickness">
        {rawThickness ? `${unit === 'mm' ? rawThickness * 10 : rawThickness} ${unit} thick` : `T ${unit}`}
      </div>
      {showZip && (
        <div className="hidden sm:flex absolute top-3 left-3 items-center gap-2 bg-white/85 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-gray-600 pointer-events-none" data-testid="zip-legend">
          <svg width="24" height="6" viewBox="0 0 24 6"><line x1="0" y1="3" x2="24" y2="3" stroke="#333" strokeWidth="2" strokeDasharray="3,2" /></svg>
          <span className="font-medium">Zip placement</span>
        </div>
      )}
      <button onClick={handleReset} className="absolute top-3 right-3 w-8 h-8 bg-white/85 backdrop-blur-sm rounded-full flex items-center justify-center text-gray-500 hover:text-gray-800 hover:bg-white transition-colors shadow-sm cursor-pointer" title="Reset view" data-testid="reset-view-btn">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 0 1-15.36 6.36"/><path d="M3 12a9 9 0 0 1 15.36-6.36"/><polyline points="21 3 21 9 15 9"/><polyline points="3 21 3 15 9 15"/></svg>
      </button>
      <InteractionHints className="hidden sm:flex absolute bottom-3 left-1/2 -translate-x-1/2 items-center gap-3 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-gray-500 pointer-events-none whitespace-nowrap" />
    </div>
  );
};

export default WaterfallBackCushion3DPreview;
